import React, { useState } from 'react';
import { Eye, Trash2, Upload, MoreHorizontal, User, Calendar, Mail } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DocumentStatusBadge } from './DocumentStatusBadge';
import { DocumentUploader } from './DocumentUploader';
import { DocumentVerificationModal } from './DocumentVerificationModal';
import { useAuthStore } from '../../store/authStore';
import type { ProjectDocument, DocumentItem as DocumentItemType, DocumentStatus } from '../../types/documents';
import { ProfileType } from '../../types';

interface DocumentItemProps {
  documentItem: DocumentItemType;
  projectDocument?: ProjectDocument;
  projectId?: string;
  projectOwnerId?: string;
  projectName?: string;
  onUpload: (file: File) => Promise<void>;
  onDelete: () => Promise<void>;
  onVerify: (status: DocumentStatus, note?: string) => Promise<void>;
  isUploading?: boolean;
  canManage?: boolean;
}

export const DocumentItem: React.FC<DocumentItemProps> = ({
  documentItem,
  projectDocument,
  projectId,
  projectOwnerId,
  projectName,
  onUpload,
  onDelete,
  onVerify,
  isUploading = false,
  canManage = false
}) => {
  const { user } = useAuthStore();
  const [showUploader, setShowUploader] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [isRequestingDocument, setIsRequestingDocument] = useState(false);

  const status = projectDocument?.status || 'MISSING';
  const hasFile = projectDocument && projectDocument.file_url;

  const canUpload = user?.profile_type === ProfileType.PORTEUR;
  const canVerify = canManage && [ProfileType.ACCOMPAGNATEUR, ProfileType.COORDINATEUR, ProfileType.ADMIN].includes(user?.profile_type as ProfileType);
  const canRequestDocument = canManage && status === 'MISSING' && projectOwnerId;

  const handleView = () => {
    if (projectDocument?.file_url) {
      window.open(projectDocument.file_url, '_blank');
    }
  };

  const handleUploadSuccess = async (file: File) => {
    await onUpload(file);
    setShowUploader(false);
  };

  const handleRequestDocument = async () => {
    if (!projectOwnerId || !user) return;

    setIsRequestingDocument(true);
    try {
      const { supabase } = await import('../../lib/supabase');

      const messageContent = `Bonjour,

Je me permets de vous contacter concernant votre projet "${projectName || 'votre projet'}".

Il manque le document suivant pour compléter votre dossier :
📄 ${documentItem.label}${documentItem.required ? ' (obligatoire)' : ''}

Formats acceptés : ${documentItem.extensions.join(', ')}
Taille maximale : ${documentItem.maxMB} MB

Merci de bien vouloir le téléverser dans l'espace Documents de votre projet dès que possible.

Si vous avez des questions ou besoin d'aide, n'hésitez pas à me contacter.

Cordialement,
${user.full_name || 'Votre accompagnateur'}`;

      const { error } = await supabase
        .from('messages')
        .insert({
          sender_id: user.id,
          recipient_id: projectOwnerId,
          subject: `Document manquant : ${documentItem.label}`,
          content: messageContent,
          project_id: projectId
        });

      if (error) throw error;

      alert('Message envoyé avec succès au porteur de projet !');
    } catch (error) {
      console.error('Error sending document request:', error);
      alert('Erreur lors de l\'envoi du message. Veuillez réessayer.');
    } finally {
      setIsRequestingDocument(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <h4 className="font-medium text-gray-900">{documentItem.label}</h4>
            {documentItem.required && (
              <span className="text-red-500 text-sm">*</span>
            )}
            {documentItem.conditional && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                Conditionnel
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">
            Formats: {documentItem.extensions.join(', ')} • Max {documentItem.maxMB}MB
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <DocumentStatusBadge status={status as any} />

          {/* Request Document Button */}
          {canRequestDocument && (
            <button
              onClick={handleRequestDocument}
              disabled={isRequestingDocument}
              className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Envoyer une demande automatique au porteur"
            >
              <Mail className="w-3.5 h-3.5" />
              <span>{isRequestingDocument ? 'Envoi...' : 'Demander'}</span>
            </button>
          )}

          {/* Actions Menu */}
          <div className="relative">
            <button
              onClick={() => setShowActions(!showActions)}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
            
            {showActions && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowActions(false)}
                />
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                  <div className="py-1">
                    {hasFile && (
                      <button
                        onClick={handleView}
                        className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Eye className="w-4 h-4" />
                        <span>Voir le document</span>
                      </button>
                    )}
                    
                    {canUpload && (
                      <button
                        onClick={() => {
                          setShowUploader(true);
                          setShowActions(false);
                        }}
                        className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Upload className="w-4 h-4" />
                        <span>{hasFile ? 'Remplacer' : 'Téléverser'}</span>
                      </button>
                    )}
                    
                    {canVerify && hasFile && status === 'UPLOADED' && (
                      <button
                        onClick={() => {
                          setShowVerificationModal(true);
                          setShowActions(false);
                        }}
                        className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-50"
                      >
                        <User className="w-4 h-4" />
                        <span>Vérifier</span>
                      </button>
                    )}
                    
                    {canUpload && hasFile && (
                      <button
                        onClick={() => {
                          onDelete();
                          setShowActions(false);
                        }}
                        className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Supprimer</span>
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* File Info */}
      {hasFile && (
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-2">
            <Eye className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-900">{projectDocument.filename}</span>
            {projectDocument.file_size && (
              <span className="text-xs text-gray-500">
                ({(projectDocument.file_size / (1024 * 1024)).toFixed(2)} MB)
              </span>
            )}
          </div>
          
          {/* Metadata */}
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            {projectDocument.uploaded_at && (
              <div className="flex items-center space-x-1">
                <Calendar className="w-3 h-3" />
                <span>
                  Déposé le {format(new Date(projectDocument.uploaded_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                </span>
              </div>
            )}
            {projectDocument.verified_at && (
              <div className="flex items-center space-x-1">
                <User className="w-3 h-3" />
                <span>
                  Vérifié le {format(new Date(projectDocument.verified_at), 'dd/MM/yyyy', { locale: fr })}
                </span>
              </div>
            )}
          </div>
          
          {/* Rejection Note */}
          {status === 'REJECTED' && projectDocument.note && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-800">
                <strong>Motif de rejet :</strong> {projectDocument.note}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Uploader */}
      {showUploader && (
        <div className="border-t border-gray-200 pt-3">
          <DocumentUploader
            documentItem={documentItem}
            onUpload={handleUploadSuccess}
            isUploading={isUploading}
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={() => setShowUploader(false)}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {showVerificationModal && (
        <DocumentVerificationModal
          document={projectDocument!}
          onClose={() => setShowVerificationModal(false)}
          onVerify={onVerify}
        />
      )}
    </div>
  );
};